import { Camera } from '@phosphor-icons/react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { downloadWithWatermark } from '@/hooks/useImageProtection';
import { toast } from 'sonner';


interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
  placeholder?: string;
  /** Enable blur-up LQIP effect while loading */
  blurPlaceholder?: boolean;
  /** Custom blur-sm data URL (tiny base64 image) */
  blurDataURL?: string;
  onLoad?: (img?: HTMLImageElement) => void;
  onError?: () => void;
  /** Enable watermark protection on download/save - default true for listing images */
  enableProtection?: boolean;
  /** Text to show on error placeholder */
  errorText?: string;
}

/**
 * OptimizedImage component with WebP/AVIF support, responsive srcset, and lazy loading
 * Provides optimal Core Web Vitals performance
 */
export function OptimizedImage({
  src,
  alt,
  className = '',
  style,
  width,
  height,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  priority = false,
  placeholder = '/placeholder.svg',
  blurPlaceholder = true,
  blurDataURL,
  onLoad,
  onError,
  enableProtection = true,
  errorText = 'Imagine indisponibilă'
}: OptimizedImageProps) {
  const normalizedSrc = (src || '').trim();
  const effectiveSrc = normalizedSrc || placeholder;

  // For external CDNs (olxcdn, etc), skip optimization - these CDNs don't support our transformations
  const isExternalCdn = effectiveSrc.includes('olxcdn.com') || effectiveSrc.includes('apollo.olxcdn');

  // Supabase public object URLs can be transformed via /render/image, but not all formats are supported.
  const isSupabasePublicObject =
    !isExternalCdn &&
    effectiveSrc.includes('supabase.co/storage/v1/object/public/');

  // Check if this is a listing image that should be protected
  const isListingImage = effectiveSrc.includes('car-listings') || effectiveSrc.includes('supabase.co/storage');
  const shouldProtect = enableProtection && isListingImage;
  
  const [imageError, setImageError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  // If Supabase image transformations fail, fall back to the original public object URL.
  const [useOriginalSrc, setUseOriginalSrc] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset local state when src changes. If the photo already finished loading
  // (e.g. server-rendered before React attached onLoad), mark it loaded.
  useEffect(() => {
    setImageError(false);
    setUseOriginalSrc(false);
    const img = imgRef.current;
    setImageLoaded(!!img && img.complete && img.naturalWidth > 0);
  }, [effectiveSrc]);

  // For hero images, preload immediately
  const isHero = priority || className.includes('hero');

  // OLX CDN URLs embed size/quality in the path: "/image;s=1200x900;q=100".
  // For faster loads (especially in Chrome/in-app browsers), request smaller variants when we know display size.
  const getExternalCdnUrl = (url: string) => {
    if (!url || !isExternalCdn) return url;

    // Only rewrite OLX-style URLs
    if (!/\/image;/.test(url)) return url;

    const targetW = Math.max(240, Math.min(1200, width || 0));
    const targetH = Math.max(180, Math.min(900, height || 0)) || Math.round(targetW * 0.75);

    return url
      .replace(/s=\d+x\d+/g, `s=${targetW}x${targetH}`)
      .replace(/q=\d+/g, 'q=80');
  };
  const handleContextMenu = useCallback(async (e: React.MouseEvent) => {
    if (!shouldProtect || !src) return;
    
    e.preventDefault();
    
    const saveImage = window.confirm(
      '📸 Dorești să salvezi această imagine?\n\nImaginea va fi descărcată cu watermark ROAutoMarket pentru protecție copyright.'
    );
    
    if (saveImage) {
      setIsDownloading(true);
      try {
        const filename = `roautomarket-${Date.now()}.jpg`;
        await downloadWithWatermark(src, filename);
        toast.success('Imagine descărcată cu succes!');
      } catch (error) {
        console.error('Error saving image:', error);
        toast.error('Eroare la salvarea imaginii. Încearcă din nou.');
      } finally {
        setIsDownloading(false);
      }
    }
  }, [shouldProtect, src]);

  // Prevent default drag behavior for protected images
  const handleDragStart = useCallback((e: React.DragEvent) => {
    if (shouldProtect) {
      e.preventDefault();
      toast.info('Pentru a salva imaginea, folosește click dreapta.');
    }
  }, [shouldProtect]);

  // Generate responsive srcset for different screen sizes with optimized widths
  const generateSrcSet = (baseSrc: string, format?: string) => {
    if (!baseSrc || baseSrc.includes('placeholder.svg')) return '';
    
    // Optimize srcset - MINIMIZE width variants to reduce Supabase image transform usage.
    // Each unique (url + width + format) counts as one billable transform.
    let widths: number[];
    
    if (width && width <= 100) {
      widths = [width]; // Single size for tiny thumbnails (retina handled by browser)
    } else if (width && width <= 200) {
      widths = [width];
    } else if (baseSrc.includes('cdn.abacus.ai')) {
      widths = [96]; // Single logo size
    } else if (baseSrc.includes('hero') || isHero) {
      widths = [640, 1280]; // 2 sizes max for hero
    } else if (baseSrc.includes('demo-images')) {
      widths = [390]; // Single demo size
    } else {
      // Car listing images: 2 sizes only (mobile + desktop)
      widths = [480, 800];
    }
    
    return widths
      .map(w => {
        const targetHeight = width && height ? Math.round(w * height / width) : undefined;
        return `${getOptimizedUrl(baseSrc, w, format, targetHeight)} ${w}w`;
      })
      .join(', ');
  };

  // Generate optimized image URL with WebP/AVIF support
  const getOptimizedUrl = (url: string, width?: number, format?: string, targetHeight?: number) => {
    if (!url || url.includes('placeholder.svg')) return url;
    
    // Handle Supabase storage URLs with proper transformation endpoint
    if (url.includes('supabase.co/storage/v1/object/public/')) {
      // Use /render/image/ endpoint for transformations (not just query params on /object/)
      const renderUrl = url.replace(
        '/storage/v1/object/public/',
        '/storage/v1/render/image/public/'
      );
      
      const params = new URLSearchParams();
      if (width) {
        params.set('width', width.toString());
      }
      if (targetHeight) {
        params.set('height', targetHeight.toString());
      }

      // NOTE: On this Supabase project, `format=webp` is rejected (400).
      // IMPORTANT: Avoid AVIF srcset for Supabase URLs to halve transform count.
      // Each format variant = separate billable transform. Use original format only.
      // (AVIF savings are marginal vs the cost of doubling transforms)

      // Single quality value for all sizes to maximize CDN cache hits
      params.set('quality', '70');
      
      return renderUrl + '?' + params.toString();
    }
    
    // Handle external CDN URLs that support transformations
    if (url.includes('cdn.abacus.ai')) {
      const params = new URLSearchParams();
      if (width) {
        // For logos, use exact display size (typically 64-96px)
        params.set('w', width.toString());
        params.set('h', Math.round(width * 0.67).toString());
      }
      params.set('f', format || 'webp'); // WebP instead of AVIF for better CDN compatibility
      params.set('q', '50'); // Lower quality for logos to reduce file size
      params.set('fit', 'contain'); // Contain instead of scale-down to preserve aspect ratio
      
      return url + (url.includes('?') ? '&' : '?') + params.toString();
    }
    
    // Handle other CDNs
    if (url.includes('cloudinary') || url.includes('imagekit')) {
      const params = new URLSearchParams();
      if (width) params.set('w', width.toString());
      if (format) params.set('f', format);
      params.set('q', '55');
      
      return url + (url.includes('?') ? '&' : '?') + params.toString();
    }
    
    // For Vite static assets (demo images, local images), add query params for Vite's image plugin
    if (url.includes('/assets/') || url.includes('demo-images')) {
      const params = new URLSearchParams();
      if (width) {
        params.set('width', width.toString());
      }
      params.set('format', format || 'avif'); // AVIF for best compression
      // Ultra aggressive quality reduction for mobile
      if (width && width <= 320) {
        params.set('quality', '25'); // Very aggressive for small mobile
      } else if (width && width <= 480) {
        params.set('quality', '28'); // Aggressive for mobile
      } else if (width && width <= 640) {
        params.set('quality', '32'); // Moderate for larger mobile
      } else {
        params.set('quality', '38'); // Desktop
      }
      
      return url + (url.includes('?') ? '&' : '?') + params.toString();
    }
    
    // For local development or simple URLs, return as-is
    return url;
  };

  const handleLoad = () => {
    setImageLoaded(true);
    onLoad?.(imgRef.current ?? undefined);
  };

  const handleError = () => {
    // Supabase transforms can fail for some images (bad metadata/corrupt file/etc.).
    // In that case, retry with the original public object URL before giving up.
    if (isSupabasePublicObject && !useOriginalSrc) {
      console.warn('[OptimizedImage] render transform failed, retrying original src', { src: optimizedSrc });
      setUseOriginalSrc(true);
      setImageLoaded(false);
      return;
    }

    // Helpful for debugging in-app browsers that block hotlinking
    console.warn('[OptimizedImage] load error', { src: optimizedSrc, isExternalCdn });
    setImageError(true);
    onError?.();
  };
  
  const sizedSrc = isExternalCdn ? getExternalCdnUrl(effectiveSrc) : effectiveSrc;

  const optimizedSrc = isExternalCdn
    ? sizedSrc
    : (useOriginalSrc
        ? sizedSrc
        : getOptimizedUrl(sizedSrc, width, isSupabasePublicObject ? undefined : 'webp', height));

  // IMPORTANT: Skip AVIF/WebP srcsets for Supabase images to minimize transform count.
  // Each format variant doubles the number of billable transforms.
  const avifSrcSet = (isExternalCdn || useOriginalSrc || isSupabasePublicObject) ? undefined : (generateSrcSet(sizedSrc, 'avif') || undefined);
  const webpSrcSet = (isExternalCdn || useOriginalSrc || isSupabasePublicObject)
    ? undefined
    : (generateSrcSet(sizedSrc, 'webp') || undefined);
  const fallbackSrcSet = (isExternalCdn || useOriginalSrc) ? undefined : (generateSrcSet(sizedSrc) || undefined);

  // Some in-app browsers (Google/Chrome Custom Tab) + external CDNs can be picky with lazy-loading.
  // For external images, load eagerly and force a referrer (origin) to avoid hotlink blocks.
  const imgLoading: 'eager' | 'lazy' = (priority || isHero || isExternalCdn) ? 'eager' : 'lazy';
  const imgFetchPriority: 'high' | 'auto' = (priority || isHero || isExternalCdn) ? 'high' : 'auto';
  const imgReferrerPolicy: React.ImgHTMLAttributes<HTMLImageElement>['referrerPolicy'] = isExternalCdn ? 'origin' : undefined;

  // Extract object-fit / object-position utilities from wrapper className
  // (needed because Tailwind object-* utilities must be on the <img>, not on the wrapper).
  const objectFitClass = (className.match(/\bobject-(contain|cover|fill|none|scale-down)\b/)?.[0]) ?? 'object-cover';
  const objectPositionClass = (className.match(/\bobject-(center|top|bottom|left|right)\b/)?.[0]) ?? '';

  // Generate a simple blur-sm placeholder color based on image type
  const getBlurBackground = () => {
    if (blurDataURL) return `url(${blurDataURL})`;
    if (effectiveSrc.includes('car-listings') || effectiveSrc.includes('auto')) {
      return 'linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--muted)/0.7) 100%)';
    }
    return 'hsl(var(--muted))';
  };

  // Show a clean error placeholder instead of broken image
  if (imageError) {
    return (
      <div className={`relative overflow-hidden ${className}`}>
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/60 text-muted-foreground">
          <Camera className="h-10 w-10 opacity-50" />
          <span className="mt-2 text-sm font-medium">{errorText}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Blur placeholder with smooth transition - LQIP effect */}
      {blurPlaceholder && !imageLoaded && (
        <div 
          className="absolute inset-0 z-0 pointer-events-none animate-pulse"
          style={{
            background: getBlurBackground(),
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: blurDataURL ? 'blur(20px)' : 'none',
            transform: blurDataURL ? 'scale(1.1)' : 'none',
            transition: 'opacity 0.3s ease-out',
          }}
        />
      )}
      
      <picture className="relative z-10 block">
        {/* AVIF format for modern browsers - best compression */}
        {avifSrcSet && (
          <source 
            type="image/avif"
            srcSet={avifSrcSet}
            sizes={sizes}
          />
        )}
        
        {/* WebP format fallback - good compression */}
        {webpSrcSet && (
          <source 
            type="image/webp"
            srcSet={webpSrcSet}
            sizes={sizes}
          />
        )}
        
        {/* Original format fallback */}
        <img
          ref={imgRef}
          src={optimizedSrc}
          srcSet={fallbackSrcSet}
          sizes={sizes}
          alt={alt}
          width={width}
          height={height}
          loading={imgLoading}
          fetchPriority={imgFetchPriority}
          decoding={isExternalCdn ? 'sync' : 'async'}
          referrerPolicy={imgReferrerPolicy}
          className={
            `w-full h-full ${objectFitClass} ${objectPositionClass} ` +
            `${shouldProtect ? 'select-none' : ''}`
          }
          onLoad={handleLoad}
          onError={handleError}
          onContextMenu={handleContextMenu}
          onDragStart={handleDragStart}
          draggable={!shouldProtect}
          style={{
            aspectRatio: width && height ? `${width}/${height}` : undefined,
            ...style
          }}
        />
      </picture>
      
      {/* No opaque spinner over critical images: it hid photos that were
          already loaded before hydration. The pulse placeholder sits beneath. */}
      
      {/* Watermark download indicator */}
      {isDownloading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm z-10">
          <div className="flex flex-col items-center gap-2 text-foreground">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Se adaugă watermark...</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Utility for preloading critical images
export const preloadImage = (src: string, priority: 'high' | 'low' = 'low') => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  link.fetchPriority = priority;
  document.head.appendChild(link);
};

// Hook for intersection observer based lazy loading
export const useIntersectionObserver = (
  threshold: number = 0.1,
  rootMargin: string = '50px'
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  useState(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      { threshold, rootMargin }
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => observer.disconnect();
  });

  return { targetRef, isIntersecting };
};